// Copyright (c) Microsoft Corporation.
// Licensed under the MIT License.

import type { AzureSupportedClouds } from "./static-helpers/cloudSettingHelpers.js";
import { AzureClouds } from "./static-helpers/cloudSettingHelpers.js";
import type {
  PageSettings,
  ContinuablePage,
  PagedAsyncIterableIterator,
} from "./static-helpers/pagingHelpers.js";

export { StorageManagementClient } from "./storageManagementClient.js";
export type { SimplePollerLike } from "./static-helpers/simplePollerHelpers.js";
export type { RestorePollerOptions } from "./restorePollerHelpers.js";
export { restorePoller } from "./restorePollerHelpers.js";
export type {
  Operation,
  OperationDisplay,
  OperationProperties,
  ServiceSpecification,
  MetricSpecification,
  Dimension,
  ErrorResponse,
  ErrorResponseBody,
  BlobContainer,
  ContainerProperties,
  PublicAccess,
  LeaseStatus,
  LeaseState,
  LeaseDuration,
  ImmutabilityPolicyProperties,
  ImmutabilityPolicyProperty,
  ImmutabilityPolicyState,
  UpdateHistoryProperty,
  ImmutabilityPolicyUpdateType,
  LegalHoldProperties,
  TagProperty,
  ProtectedAppendWritesHistory,
  ImmutableStorageWithVersioning,
  MigrationState,
  ProxyResource,
  Resource,
  SystemData,
  CreatedByType,
  LegalHold,
  LeaseContainerRequest,
  LeaseContainerRequestAction,
  LeaseContainerResponse,
  CloudError,
  CloudErrorBody,
  ErrorDetail,
  ErrorAdditionalInfo,
  ListContainerItem,
  AzureEntityResource,
  ImmutabilityPolicy,
  BlobServiceProperties,
  BlobServicePropertiesProperties,
  CorsRules,
  CorsRule,
  AllowedMethods,
  DeleteRetentionPolicy,
  StaticWebsite,
  ChangeFeed,
  RestorePolicyProperties,
  LastAccessTimeTrackingPolicy,
  Name,
  Sku,
  SkuName,
  SkuTier,
  StorageAccountCheckNameAvailabilityParameters,
  CheckNameAvailabilityResult,
  Reason,
  StorageAccount,
  StorageAccountProperties,
  ProvisioningState,
  Endpoints,
  StorageAccountMicrosoftEndpoints,
  StorageAccountInternetEndpoints,
  StorageAccountIpv6Endpoints,
  AccountStatus,
  CustomDomain,
  SasPolicy,
  ExpirationAction,
  KeyPolicy,
  KeyCreationTime,
  Encryption,
  EncryptionServices,
  EncryptionService,
  KeyType,
  KeySource,
  KeyVaultProperties,
  EncryptionIdentity,
  AccessTier,
  AzureFilesIdentityBasedAuthentication,
  DirectoryServiceOptions,
  ActiveDirectoryProperties,
  AccountType,
  DefaultSharePermission,
  SmbOAuthSettings,
  NetworkRuleSet,
  Bypass,
  ResourceAccessRule,
  VirtualNetworkRule,
  State,
  IPRule,
  DefaultAction,
  GeoReplicationStats,
  GeoReplicationStatus,
  PostFailoverRedundancy,
  PostPlannedFailoverRedundancy,
  LargeFileSharesState,
  PrivateEndpointConnection,
  PrivateEndpointConnectionProperties,
  PrivateEndpoint,
  PrivateLinkServiceConnectionState,
  PrivateEndpointServiceConnectionStatus,
  PrivateEndpointConnectionProvisioningState,
  RoutingPreference,
  RoutingChoice,
  DualStackEndpointPreference,
  BlobRestoreStatus,
  BlobRestoreProgressStatus,
  BlobRestoreParameters,
  BlobRestoreRange,
  MinimumTlsVersion,
  PublicNetworkAccess,
  ImmutableStorageAccount,
  AccountImmutabilityPolicyProperties,
  AccountImmutabilityPolicyState,
  AllowedCopyScope,
  StorageAccountSkuConversionStatus,
  SkuConversionStatus,
  DnsEndpointType,
  GeoPriorityReplicationStatus,
  StorageAccountSharedKeyAccessProperties,
  ServiceSharedKeyAccessProperties,
  StorageDataCollaborationPolicyProperties,
  Kind,
  Identity,
  IdentityType,
  UserAssignedIdentity,
  ExtendedLocation,
  ExtendedLocationTypes,
  Placement,
  ZonePlacementPolicy,
  TrackedResource,
  StorageAccountCreateParameters,
  StorageAccountPropertiesCreateParameters,
  StorageAccountUpdateParameters,
  StorageAccountPropertiesUpdateParameters,
  StorageAccountListKeysResult,
  StorageAccountKey,
  KeyPermission,
  StorageAccountRegenerateKeyParameters,
  AccountSasParameters,
  Services,
  SignedResourceTypes,
  Permissions,
  HttpProtocol,
  ListAccountSasResponse,
  ServiceSasParameters,
  SignedResource,
  ListServiceSasResponse,
  StorageAccountMigration,
  StorageAccountMigrationProperties,
  MigrationStatus,
  MigrationName,
  FileShare,
  FileShareProperties,
  EnabledProtocols,
  RootSquashType,
  ShareAccessTier,
  SignedIdentifier,
  AccessPolicy,
  FileSharePropertiesFileSharePaidBursting,
  DeletedShare,
  LeaseShareRequest,
  LeaseShareAction,
  LeaseShareResponse,
  FileShareItem,
  FileServiceProperties,
  FileServicePropertiesProperties,
  ProtocolSettings,
  SmbSetting,
  Multichannel,
  EncryptionInTransit,
  NfsSetting,
  FileServiceItems,
  FileServiceUsage,
  FileServiceUsageProperties,
  AccountLimits,
  FileShareLimits,
  FileShareRecommendations,
  BurstingConstants,
  AccountUsage,
  AccountUsageElements,
  QueueServiceProperties,
  QueueServicePropertiesProperties,
  ListQueueServices,
  DeletedAccount,
  DeletedAccountProperties,
  ManagementPolicy,
  ManagementPolicyProperties,
  ManagementPolicySchema,
  ManagementPolicyRule,
  RuleType,
  ManagementPolicyDefinition,
  ManagementPolicyAction,
  ManagementPolicyBaseBlob,
  DateAfterModification,
  ManagementPolicySnapShot,
  DateAfterCreation,
  ManagementPolicyVersion,
  ManagementPolicyFilter,
  TagFilter,
  ManagementPolicyName,
  BlobInventoryPolicy,
  BlobInventoryPolicyProperties,
  BlobInventoryPolicySchema,
  InventoryRuleType,
  BlobInventoryPolicyRule,
  BlobInventoryPolicyDefinition,
  BlobInventoryPolicyFilter,
  BlobInventoryCreationTime,
  Format,
  Schedule,
  ObjectType,
  BlobInventoryPolicyName,
  EncryptionScope,
  EncryptionScopeProperties,
  EncryptionScopeSource,
  EncryptionScopeState,
  EncryptionScopeKeyVaultProperties,
  TableServiceProperties,
  TableServicePropertiesProperties,
  ListTableServices,
  NetworkSecurityPerimeterConfiguration,
  NetworkSecurityPerimeterConfigurationProperties,
  NetworkSecurityPerimeterConfigurationProvisioningState,
  ProvisioningIssue,
  ProvisioningIssueProperties,
  IssueType,
  Severity,
  NetworkSecurityPerimeter,
  NetworkSecurityPerimeterConfigurationPropertiesResourceAssociation,
  ResourceAssociationAccessMode,
  NetworkSecurityPerimeterConfigurationPropertiesProfile,
  NspAccessRule,
  NspAccessRuleProperties,
  NspAccessRuleDirection,
  NspAccessRulePropertiesSubscriptionsItem,
  StorageTaskAssignment,
  StorageTaskAssignmentProperties,
  StorageTaskAssignmentExecutionContext,
  ExecutionTarget,
  ExecutionTrigger,
  TriggerType,
  TriggerParameters,
  IntervalUnit,
  StorageTaskAssignmentReport,
  StorageTaskAssignmentProvisioningState,
  StorageTaskReportProperties,
  RunStatusEnum,
  RunResult,
  StorageTaskAssignmentUpdateParameters,
  StorageTaskAssignmentUpdateProperties,
  StorageTaskAssignmentUpdateExecutionContext,
  ExecutionTriggerUpdate,
  TriggerParametersUpdate,
  StorageTaskAssignmentUpdateReport,
  ErrorResponse_1,
  Connector,
  StorageConnectorProperties,
  StorageConnectorState,
  StorageConnectorDataSourceType,
  StorageConnectorSource,
  StorageConnectorSourceUnion,
  StorageConnectorSourceType,
  DataShareSource,
  StorageConnectorConnection,
  StorageConnectorConnectionUnion,
  StorageConnectorConnectionType,
  DataShareConnection,
  StorageConnectorAuthProperties,
  StorageConnectorAuthPropertiesUnion,
  StorageConnectorAuthType,
  ManagedIdentityAuthProperties,
  NativeDataSharingProvisioningState,
  ConnectorUpdate,
  StorageConnectorPropertiesUpdate,
  StorageConnectorSourceUpdate,
  StorageConnectorSourceUpdateUnion,
  DataShareSourceUpdate,
  StorageConnectorAuthPropertiesUpdate,
  StorageConnectorAuthPropertiesUpdateUnion,
  ManagedIdentityAuthPropertiesUpdate,
  TrackedResourceUpdate,
  TestExistingConnectionRequest,
  TestConnectionResponse,
  DataShare,
  StorageDataShareProperties,
  StorageDataShareAccessPolicy,
  StorageDataShareAccessPolicyPermission,
  StorageDataShareAsset,
  DataShareUpdate,
  StorageDataSharePropertiesUpdate,
  AdvancedPlatformMetricsRule,
  AdvancedPlatformMetricsRuleProperties,
  AdvancedPlatformMetricsRuleType,
  MetricsEmitted,
  AdvancedPlatformMetricsRuleConfig,
  AdvancedPlatformMetricsFilterType,
  PrivateLinkResourceListResult,
  PrivateLinkResource,
  PrivateLinkResourceProperties,
  StorageTaskReportInstance,
  ListQueue,
  ListQueueProperties,
  StorageQueue,
  QueueProperties,
  ObjectReplicationPolicy,
  ObjectReplicationPolicyProperties,
  ObjectReplicationPolicyRule,
  ObjectReplicationPolicyFilter,
  ObjectReplicationPolicyPropertiesMetrics,
  ObjectReplicationPolicyPropertiesPriorityReplication,
  ObjectReplicationPolicyPropertiesTagsReplication,
  LocalUser,
  LocalUserProperties,
  PermissionScope,
  SshPublicKey,
  LocalUserKeys,
  LocalUserRegeneratePasswordResult,
  Table,
  TableProperties,
  TableSignedIdentifier,
  TableAccessPolicy,
  SkuInformation,
  SkuInformationLocationInfoItem,
  SKUCapability,
  Restriction,
  ReasonCode,
  Usage,
  UsageUnit,
  UsageName,
  ListContainersInclude,
  StorageAccountExpand,
  ListEncryptionScopesInclude,
  ListLocalUserIncludeParam,
} from "./models/index.js";
export {
  KnownLeaseStatus,
  KnownLeaseState,
  KnownLeaseDuration,
  KnownImmutabilityPolicyState,
  KnownImmutabilityPolicyUpdateType,
  KnownMigrationState,
  KnownCreatedByType,
  KnownLeaseContainerRequestAction,
  KnownAllowedMethods,
  KnownName,
  KnownSkuName,
  KnownExpirationAction,
  KnownKeyType,
  KnownKeySource,
  KnownDirectoryServiceOptions,
  KnownAccountType,
  KnownDefaultSharePermission,
  KnownBypass,
  KnownState,
  KnownGeoReplicationStatus,
  KnownPostFailoverRedundancy,
  KnownPostPlannedFailoverRedundancy,
  KnownLargeFileSharesState,
  KnownPrivateEndpointServiceConnectionStatus,
  KnownPrivateEndpointConnectionProvisioningState,
  KnownRoutingChoice,
  KnownBlobRestoreProgressStatus,
  KnownMinimumTlsVersion,
  KnownPublicNetworkAccess,
  KnownAccountImmutabilityPolicyState,
  KnownAllowedCopyScope,
  KnownSkuConversionStatus,
  KnownDnsEndpointType,
  KnownKind,
  KnownIdentityType,
  KnownExtendedLocationTypes,
  KnownZonePlacementPolicy,
  KnownServices,
  KnownSignedResourceTypes,
  KnownPermissions,
  KnownSignedResource,
  KnownMigrationStatus,
  KnownMigrationName,
  KnownEnabledProtocols,
  KnownRootSquashType,
  KnownShareAccessTier,
  KnownLeaseShareAction,
  KnownRuleType,
  KnownManagementPolicyName,
  KnownInventoryRuleType,
  KnownFormat,
  KnownSchedule,
  KnownObjectType,
  KnownBlobInventoryPolicyName,
  KnownEncryptionScopeSource,
  KnownEncryptionScopeState,
  KnownNetworkSecurityPerimeterConfigurationProvisioningState,
  KnownIssueType,
  KnownSeverity,
  KnownResourceAssociationAccessMode,
  KnownNspAccessRuleDirection,
  KnownTriggerType,
  KnownIntervalUnit,
  KnownStorageTaskAssignmentProvisioningState,
  KnownRunStatusEnum,
  KnownRunResult,
  KnownStorageConnectorState,
  KnownStorageConnectorDataSourceType,
  KnownStorageConnectorSourceType,
  KnownStorageConnectorConnectionType,
  KnownStorageConnectorAuthType,
  KnownNativeDataSharingProvisioningState,
  KnownStorageDataShareAccessPolicyPermission,
  KnownAdvancedPlatformMetricsRuleType,
  KnownMetricsEmitted,
  KnownAdvancedPlatformMetricsFilterType,
  KnownReasonCode,
  KnownListContainersInclude,
  KnownListEncryptionScopesInclude,
  KnownListLocalUserIncludeParam,
  KnownVersions,
} from "./models/index.js";
export type { StorageManagementClientOptionalParams } from "./api/index.js";
export type {
  AdvancedPlatformMetricsListOptionalParams,
  AdvancedPlatformMetricsDeleteOptionalParams,
  AdvancedPlatformMetricsCreateOrUpdateOptionalParams,
  AdvancedPlatformMetricsGetOptionalParams,
} from "./api/advancedPlatformMetrics/index.js";
export type {
  BlobContainersExtendImmutabilityPolicyOptionalParams,
  BlobContainersLockImmutabilityPolicyOptionalParams,
  BlobContainersDeleteImmutabilityPolicyOptionalParams,
  BlobContainersCreateOrUpdateImmutabilityPolicyOptionalParams,
  BlobContainersGetImmutabilityPolicyOptionalParams,
  BlobContainersListOptionalParams,
  BlobContainersObjectLevelWormOptionalParams,
  BlobContainersLeaseOptionalParams,
  BlobContainersClearLegalHoldOptionalParams,
  BlobContainersSetLegalHoldOptionalParams,
  BlobContainersDeleteOptionalParams,
  BlobContainersUpdateOptionalParams,
  BlobContainersCreateOptionalParams,
  BlobContainersGetOptionalParams,
} from "./api/blobContainers/index.js";
export type {
  BlobInventoryPoliciesListOptionalParams,
  BlobInventoryPoliciesDeleteOptionalParams,
  BlobInventoryPoliciesCreateOrUpdateOptionalParams,
  BlobInventoryPoliciesGetOptionalParams,
} from "./api/blobInventoryPolicies/index.js";
export type {
  BlobServicesListOptionalParams,
  BlobServicesSetServicePropertiesOptionalParams,
  BlobServicesGetServicePropertiesOptionalParams,
} from "./api/blobServices/index.js";
export type {
  ConnectorsTestExistingConnectionOptionalParams,
  ConnectorsListByStorageAccountOptionalParams,
  ConnectorsDeleteOptionalParams,
  ConnectorsUpdateOptionalParams,
  ConnectorsCreateOptionalParams,
  ConnectorsGetOptionalParams,
} from "./api/connectors/index.js";
export type {
  DataSharesListByStorageAccountOptionalParams,
  DataSharesDeleteOptionalParams,
  DataSharesUpdateOptionalParams,
  DataSharesCreateOptionalParams,
  DataSharesGetOptionalParams,
} from "./api/dataShares/index.js";
export type {
  DeletedAccountsListOptionalParams,
  DeletedAccountsGetOptionalParams,
} from "./api/deletedAccounts/index.js";
export type {
  EncryptionScopesListOptionalParams,
  EncryptionScopesPatchOptionalParams,
  EncryptionScopesPutOptionalParams,
  EncryptionScopesGetOptionalParams,
} from "./api/encryptionScopes/index.js";
export type {
  FileServicesListServiceUsagesOptionalParams,
  FileServicesGetServiceUsageOptionalParams,
  FileServicesListOptionalParams,
  FileServicesSetServicePropertiesOptionalParams,
  FileServicesGetServicePropertiesOptionalParams,
} from "./api/fileServices/index.js";
export type {
  FileSharesListOptionalParams,
  FileSharesLeaseOptionalParams,
  FileSharesRestoreOptionalParams,
  FileSharesDeleteOptionalParams,
  FileSharesUpdateOptionalParams,
  FileSharesCreateOptionalParams,
  FileSharesGetOptionalParams,
} from "./api/fileShares/index.js";
export type {
  LocalUsersRegeneratePasswordOptionalParams,
  LocalUsersListKeysOptionalParams,
  LocalUsersListOptionalParams,
  LocalUsersDeleteOptionalParams,
  LocalUsersCreateOrUpdateOptionalParams,
  LocalUsersGetOptionalParams,
} from "./api/localUsers/index.js";
export type {
  ManagementPoliciesDeleteOptionalParams,
  ManagementPoliciesCreateOrUpdateOptionalParams,
  ManagementPoliciesGetOptionalParams,
} from "./api/managementPolicies/index.js";
export type {
  NetworkSecurityPerimeterConfigurationsReconcileOptionalParams,
  NetworkSecurityPerimeterConfigurationsListOptionalParams,
  NetworkSecurityPerimeterConfigurationsGetOptionalParams,
} from "./api/networkSecurityPerimeterConfigurations/index.js";
export type {
  ObjectReplicationPoliciesListOptionalParams,
  ObjectReplicationPoliciesDeleteOptionalParams,
  ObjectReplicationPoliciesCreateOrUpdateOptionalParams,
  ObjectReplicationPoliciesGetOptionalParams,
} from "./api/objectReplicationPolicies/index.js";
export type { OperationsListOptionalParams } from "./api/operations/index.js";
export type {
  PrivateEndpointConnectionsListOptionalParams,
  PrivateEndpointConnectionsDeleteOptionalParams,
  PrivateEndpointConnectionsPutOptionalParams,
  PrivateEndpointConnectionsGetOptionalParams,
} from "./api/privateEndpointConnections/index.js";
export type { PrivateLinkResourcesListByStorageAccountOptionalParams } from "./api/privateLinkResources/index.js";
export type {
  QueueDeleteOptionalParams,
  QueueUpdateOptionalParams,
  QueueCreateOptionalParams,
  QueueGetOptionalParams,
  QueueListOptionalParams,
} from "./api/queue/index.js";
export type {
  QueueServicesListOptionalParams,
  QueueServicesSetServicePropertiesOptionalParams,
  QueueServicesGetServicePropertiesOptionalParams,
} from "./api/queueServices/index.js";
export type { SkusListOptionalParams } from "./api/skus/index.js";
export type {
  StorageAccountsGetCustomerInitiatedMigrationOptionalParams,
  StorageAccountsRevokeUserDelegationKeysOptionalParams,
  StorageAccountsRestoreBlobRangesOptionalParams,
  StorageAccountsCustomerInitiatedMigrationOptionalParams,
  StorageAccountsAbortHierarchicalNamespaceMigrationOptionalParams,
  StorageAccountsHierarchicalNamespaceMigrationOptionalParams,
  StorageAccountsFailoverOptionalParams,
  StorageAccountsListServiceSASOptionalParams,
  StorageAccountsListAccountSASOptionalParams,
  StorageAccountsRegenerateKeyOptionalParams,
  StorageAccountsListKeysOptionalParams,
  StorageAccountsListOptionalParams,
  StorageAccountsListByResourceGroupOptionalParams,
  StorageAccountsDeleteOptionalParams,
  StorageAccountsUpdateOptionalParams,
  StorageAccountsCreateOptionalParams,
  StorageAccountsGetPropertiesOptionalParams,
  StorageAccountsCheckNameAvailabilityOptionalParams,
} from "./api/storageAccounts/index.js";
export type { StorageTaskAssignmentInstancesReportListOptionalParams } from "./api/storageTaskAssignmentInstancesReport/index.js";
export type {
  StorageTaskAssignmentsStopAssignmentOptionalParams,
  StorageTaskAssignmentsListOptionalParams,
  StorageTaskAssignmentsDeleteOptionalParams,
  StorageTaskAssignmentsUpdateOptionalParams,
  StorageTaskAssignmentsCreateOptionalParams,
  StorageTaskAssignmentsGetOptionalParams,
} from "./api/storageTaskAssignments/index.js";
export type { StorageTaskAssignmentsInstancesReportListOptionalParams } from "./api/storageTaskAssignmentsInstancesReport/index.js";
export type {
  TableListOptionalParams,
  TableDeleteOptionalParams,
  TableUpdateOptionalParams,
  TableCreateOptionalParams,
  TableGetOptionalParams,
} from "./api/table/index.js";
export type {
  TableServicesListOptionalParams,
  TableServicesSetServicePropertiesOptionalParams,
  TableServicesGetServicePropertiesOptionalParams,
} from "./api/tableServices/index.js";
export type { UsagesListByLocationOptionalParams } from "./api/usages/index.js";
export type {
  AdvancedPlatformMetricsOperations,
  BlobContainersOperations,
  BlobInventoryPoliciesOperations,
  BlobServicesOperations,
  ConnectorsOperations,
  DataSharesOperations,
  DeletedAccountsOperations,
  EncryptionScopesOperations,
  FileServicesOperations,
  FileSharesOperations,
  LocalUsersOperations,
  ManagementPoliciesOperations,
  NetworkSecurityPerimeterConfigurationsOperations,
  ObjectReplicationPoliciesOperations,
  OperationsOperations,
  PrivateEndpointConnectionsOperations,
  PrivateLinkResourcesOperations,
  QueueOperations,
  QueueServicesOperations,
  SkusOperations,
  StorageAccountsOperations,
  StorageTaskAssignmentInstancesReportOperations,
  StorageTaskAssignmentsOperations,
  StorageTaskAssignmentsInstancesReportOperations,
  TableOperations,
  TableServicesOperations,
  UsagesOperations,
} from "./classic/index.js";
export type { PageSettings, ContinuablePage, PagedAsyncIterableIterator };
export { AzureClouds };
export type { AzureSupportedClouds };
export { RestError, isRestError } from "@azure/core-rest-pipeline";
